import { Action } from "redux";
import { LogoCategory, ThunkAction } from "../types/index.js";
import { ModuleNotFoundError } from "../utils/utils.js";
import { debouncedPatchModuleRequest } from "./Modules.js";
import { isActionType } from "./utils.js";

const ActionTypeKeys = [
  "MOVE_LOGO_START",
  "UPSERT_LOGO_START",
  "DELETE_LOGO_START",
] as const;

type ActionType = (typeof ActionTypeKeys)[number];
type ActionBase<T extends ActionType> = Action<T>;

type MoveLogoStart = ActionBase<"MOVE_LOGO_START"> & {
  moduleId: string;
  logoCategory: LogoCategory;
  dragIndex: number;
  hoverIndex: number;
};

type UpsertLogoStart = ActionBase<"UPSERT_LOGO_START"> & {
  moduleId: string;
  logoCategory: LogoCategory;
  newLogoId: string;
  previousLogoId: string | undefined;
};

type DeleteLogoStart = ActionBase<"DELETE_LOGO_START"> & {
  moduleId: string;
  logoCategory: LogoCategory;
  logoId: string;
};

export type LogosAction = MoveLogoStart | UpsertLogoStart | DeleteLogoStart;

export const isLogosAction = (action: Action): action is LogosAction =>
  isActionType(action, ActionTypeKeys);

export const changeLogo = (
  siteId: string,
  action: LogosAction,
): ThunkAction<void> => {
  return (dispatch, getState) => {
    dispatch(action);

    const module = getState().modules.byId[action.moduleId];
    if (!module) throw new ModuleNotFoundError(action.moduleId);

    debouncedPatchModuleRequest({
      dispatch,
      getState,
      siteId,
      moduleId: action.moduleId,
      patch: { settings: module.settings },
    });
  };
};

export const moveLogoStart = (
  moduleId: string,
  logoCategory: LogoCategory,
  dragIndex: number,
  hoverIndex: number,
): MoveLogoStart => ({
  type: "MOVE_LOGO_START",
  moduleId,
  logoCategory,
  dragIndex,
  hoverIndex,
});

export const upsertLogoStart = (
  moduleId: string,
  logoCategory: LogoCategory,
  newLogoId: string,
  previousLogoId: string | undefined,
): UpsertLogoStart => ({
  type: "UPSERT_LOGO_START",
  moduleId,
  logoCategory,
  newLogoId,
  previousLogoId,
});

export const deleteLogoStart = (
  moduleId: string,
  logoCategory: LogoCategory,
  logoId: string,
): DeleteLogoStart => ({
  type: "DELETE_LOGO_START",
  moduleId,
  logoCategory,
  logoId,
});
